'use client';

import sendAiContentDownloadEvent from 'components/components/events/AiContentDownloadEvent';
import { type JSX, useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Slider, Text } from '@videoblocks/design-system';
import { AiContentVoiceoverType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentVoiceoverTypeEnum';
import { Bin, DownloadBottom, Pause, Play } from '@videoblocks/react-icons';

import CircleSpinner from '../../../new/components/CircleSpinner';
import { dispatchAudioPlay, generateAudioInstanceId } from '../_hooks/AudioPlayEvent';
import { useAudioCoordination } from '../_hooks/useAudioCoordination';

export type GeneratedVoicePlayerProps = {
    audioUrl: string;
    title: string;
    header?: string;
    subtitle?: string;
    onDelete?: () => void;
    script?: string | null;
    hasPlayed: boolean;
    setHasPlayed: () => void;
    isComplete?: boolean;
    isFetching?: boolean;
    // event tracking properties
    aiContentId?: string | null;
    lastAiContentId?: string | null;
    voiceId?: string | null;
    aiContentVoiceoverType?: keyof typeof AiContentVoiceoverType;
    aiModelName?: string | null;
};

const GeneratedVoicePlayer = ({
    audioUrl,
    title,
    header,
    subtitle,
    onDelete,
    script,
    hasPlayed,
    setHasPlayed,
    isComplete = true,
    isFetching = false,
    aiContentId,
    lastAiContentId,
    voiceId,
    aiContentVoiceoverType,
    aiModelName,
}: GeneratedVoicePlayerProps): JSX.Element => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const animationFrameRef = useRef<number | null>(null);
    // Generate a stable unique instance ID for audio coordination
    const instanceIdRef = useRef(generateAudioInstanceId());
    // Refs kept current on every render to avoid stale closures in stable effects
    const hasPlayedRef = useRef(hasPlayed);
    const setHasPlayedRef = useRef(setHasPlayed);
    const audioUrlRef = useRef(audioUrl);
    const isCompleteRef = useRef(isComplete);
    // When audioUrl changes while audio is playing we defer the load() call until the audio ends
    const pendingLoadRef = useRef(false);

    useEffect(() => {
        hasPlayedRef.current = hasPlayed;
        setHasPlayedRef.current = setHasPlayed;
        audioUrlRef.current = audioUrl;
        isCompleteRef.current = isComplete;
    });

    const [isPlaying, _setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isLoading, setIsLoading] = useState(!!audioUrl);

    const setIsPlaying = (playing: boolean): void => {
        if (playing) {
            dispatchAudioPlay(audioUrl, instanceIdRef.current);
        }
        _setIsPlaying(playing);
    };

    const handlePause = useCallback((): void => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
        // Call _setIsPlaying directly to avoid stale closure with setIsPlaying wrapper
        // No need to dispatch AudioPlayEvent when pausing (only when playing)
        _setIsPlaying(false);
    }, []);

    // Pass the ref object (not .current) so useAudioCoordination reads it inside effects
    useAudioCoordination({
        instanceIdRef,
        onPause: handlePause,
    });

    const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

    const updateProgress = () => {
        if (!audioRef.current || !isPlaying) return;

        const audio = audioRef.current;

        setCurrentTime(audio.currentTime);

        animationFrameRef.current = requestAnimationFrame(updateProgress);
    };

    const togglePlayPause = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            void audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const generateDownloadFilename = () => {
        const filenameComponents = [title];
        if (script) {
            filenameComponents.push(script.substring(0, 15).replace(/\s+/g, '_') + '...');
        }

        return `${filenameComponents.join('_')}.mp3`;
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = audioUrl;
        link.download = generateDownloadFilename();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (aiContentId && aiContentVoiceoverType) {
            void sendAiContentDownloadEvent(
                aiContentId,
                voiceId ?? null,
                aiContentVoiceoverType,
                window.location.href,
                document.referrer || undefined,
                lastAiContentId ?? null,
                aiModelName ?? null,
            );
        }
    };

    const handleSliderChange = (newValue: number[]) => {
        if (audioRef.current) {
            const newTime = (newValue[0] / 100) * duration;
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    useEffect(() => {
        if (isPlaying) {
            animationFrameRef.current = requestAnimationFrame(updateProgress);
        } else {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        }

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isPlaying, duration]);

    // Loads the audio element with the given URL and wires up auto-play.
    // Extracted so it can be called both from the audioUrl effect and from handleEnded
    const loadAudio = useCallback((url: string) => {
        const audio = audioRef.current;
        if (!audio) return;

        // Manage src directly — letting React set <audio src=…> would trigger the browser's
        // resource-selection algorithm immediately, aborting any in-progress MediaSource stream
        audio.src = url;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsLoading(true);
        setCurrentTime(0);
        audio.load();

        const handleCanPlay = () => {
            if (audioRef.current) {
                if (!hasPlayedRef.current) {
                    audioRef.current
                        .play()
                        .then(() => {
                            setIsPlaying(true);
                        })
                        .catch((error) => {
                            console.log('Auto-play prevented by browser:', error);
                            setIsPlaying(false);
                        });
                }
                setIsLoading(false);
            }
        };

        audio.addEventListener('canplay', handleCanPlay, { once: true });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // When audioUrl changes (e.g. new generation, or MediaSource URL → Blob URL swap):
    //   • If audio is actively playing, defer the load so we don't cut off streaming playback.
    //     handleEnded will pick it up once the current audio finishes naturally
    //   • hasPlayed is intentionally excluded from deps — including it would re-run load() after
    //     the audio ends (when setHasPlayed fires), invalidating the MediaSource blob URL
    useEffect(() => {
        if (!audioUrl) return;
        const audio = audioRef.current;
        if (!audio) return;

        if (!audio.paused) {
            if (!isCompleteRef.current) {
                // New generation started — interrupt current playback immediately
                audio.pause();
                _setIsPlaying(false);
                // fall through to loadAudio below
            } else {
                // MediaSource → Blob URL swap for the same stream — defer until audio ends naturally
                pendingLoadRef.current = true;
                return;
            }
        }

        pendingLoadRef.current = false;
        loadAudio(audioUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioUrl]);

    // When a non-streaming generation starts, isFetching becomes true before the new URL
    // arrives. Pausing here ensures the audio element is idle when the new URL lands, so
    // the [audioUrl] effect loads it immediately rather than deferring.
    useEffect(() => {
        if (!isFetching) return;
        const audio = audioRef.current;
        if (!audio || audio.paused) return;
        audio.pause();
        _setIsPlaying(false);
    }, [isFetching]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        // durationchange fires when MediaSource finalises the duration after endOfStream(),
        // giving us a working timeline even while playing the streaming source
        const handleDurationUpdate = () => {
            if (isFinite(audio.duration) && audio.duration > 0) {
                setDuration(audio.duration);
            }
        };

        const handleEnded = () => {
            // Use refs so this stable callback always sees the latest values
            _setIsPlaying(false);
            setHasPlayedRef.current?.();
            setCurrentTime(0);
            if (audio.currentTime !== 0) {
                audio.currentTime = 0;
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            // If the URL was swapped while audio was playing (e.g. MediaSource → Blob URL),
            // load the new URL now that playback has finished naturally
            if (pendingLoadRef.current) {
                pendingLoadRef.current = false;
                loadAudio(audioUrlRef.current);
            }
        };

        const handleLoadStart = () => {
            setIsLoading(true);
        };

        audio.addEventListener('loadedmetadata', handleDurationUpdate);
        audio.addEventListener('durationchange', handleDurationUpdate);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('loadstart', handleLoadStart);

        return () => {
            audio.removeEventListener('loadedmetadata', handleDurationUpdate);
            audio.removeEventListener('durationchange', handleDurationUpdate);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('loadstart', handleLoadStart);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="w-full mx-auto mt-8">
            {/* src is managed imperatively via audioRef to avoid React interrupting active playback */}
            <audio ref={audioRef} preload="metadata" />

            <div
                className={twMerge(
                    'bg-gray-900 pb-10 rounded-xl p-4 border-2 border-white',
                    header && 'pb-[38px]', // this ensures the main content is centered by adding the bottom padding + header text height
                )}
            >
                {header && (
                    <Text fontSize="base" fontWeight="regular" className="">
                        {header}
                    </Text>
                )}
                <div className="flex items-center gap-4">
                    <button
                        onClick={togglePlayPause}
                        disabled={isFetching || isLoading}
                        data-testid="play-pause"
                        className={twMerge(
                            'flex-shrink-0 w-12 h-12 hover:bg-blue-600 rounded-full flex items-center justify-center text-white transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer',
                            isPlaying ? 'bg-blue-500' : 'bg-gray-400',
                        )}
                    >
                        {isLoading ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        ) : isPlaying ? (
                            <Pause className="w-6 h-6 fill-current" />
                        ) : (
                            <Play className="w-6 h-6 fill-current ml-0.5" />
                        )}
                    </button>

                    <div className="flex-shrink-0 min-w-0">
                        <h3 className="text-white font-medium text-sm truncate">{title}</h3>
                        <p className="text-gray-400 text-xs truncate">{subtitle}</p>
                    </div>

                    {isComplete ? (
                        <Slider
                            value={[progressPercentage]}
                            onValueChange={handleSliderChange}
                            className="mx-4"
                            trackProps={{ className: 'bg-white' }}
                            rangeProps={{ className: 'bg-blue-600' }}
                        />
                    ) : (
                        <div className="mx-4 flex-1 h-1 rounded-full bg-white shimmer shimmer-bg shimmer-color-blue-400 shimmer-repeat-delay-0" />
                    )}

                    {!onDelete ? (
                        <button
                            onClick={handleDownload}
                            disabled={!isComplete}
                            className="flex-shrink-0 w-10 h-10 bg-yellow-400 hover:bg-yellow-500 rounded-full flex items-center justify-center text-white transition-all duration-200 transform cursor-pointer hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={isComplete ? 'Download audio' : 'Generating…'}
                        >
                            {isComplete ? (
                                <DownloadBottom className="w-4 h-4" />
                            ) : (
                                <CircleSpinner className="w-4 h-4 fill-white text-yellow-300" />
                            )}
                        </button>
                    ) : (
                        <button
                            onClick={onDelete}
                            className="flex-shrink-0 w-10 h-10 bg-yellow-400 hover:bg-yellow-500 rounded-full flex items-center justify-center text-white transition-all duration-200 transform cursor-pointer hover:scale-105"
                            title="Delete audio"
                        >
                            <Bin className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GeneratedVoicePlayer;
